import React from 'react';
import { cx } from '@milanote/roma/utils';
import { LoadingIndicator } from '@milanote/roma';

import { useTranslator } from '../../../../../components/i18n/useTranslator';
import Toast from '../../../../../structural/toast/Toast';
import type { ImageModalV2OperationToastState } from './imageModalV2OperationToastTypes';
import { IMAGE_MODAL_V2_TOAST_EXIT_FALLBACK_MS } from '../imageModalV2Constants';
import { useImageModalV2SurfaceLifecycle } from './useImageModalV2SurfaceLifecycle';

import './ImageModalV2OperationToast.scss';

/**
 * Modal-owned operation toast (background removal, upload, save milestones) whose enter/exit
 * participates in the bottom-surface handoff. The caller decides the exit handoff hold via
 * `exitHandoffMs`, since it is the one that knows the semantics of each `toast.message`.
 */
const ImageModalV2OperationToast: React.FC<{
    toast: ImageModalV2OperationToastState;
    isOpen: boolean;
    /** Extra hold before `onExitComplete` fires once this toast has finished exiting. Defaults to none. */
    exitHandoffMs?: number;
    onExitComplete: () => void;
    onEnterComplete?: () => void;
}> = ({ toast, isOpen, exitHandoffMs, onExitComplete, onEnterComplete }) => {
    const translate = useTranslator('popup', 'imageModal.backgroundRemovalToast');
    const { isShown, hasEntered, handleTransitionEnd } = useImageModalV2SurfaceLifecycle({
        isOpen,
        fallbackMs: IMAGE_MODAL_V2_TOAST_EXIT_FALLBACK_MS,
        exitHandoffMs,
        onExitComplete,
        onEnterComplete,
    });

    const content = (() => {
        switch (toast.message) {
            case 'removingBackground':
                return <LoadingIndicator size="xs">{translate('removingBackground')}</LoadingIndicator>;
            case 'savingChanges':
                return <LoadingIndicator size="xs">{translate('savingChanges')}</LoadingIndicator>;
        }
    })();

    return (
        <Toast
            className={cx('ImageModalV2OperationToast', isShown && 'is-shown', hasEntered && 'has-entered')}
            groupId="ImageModalV2"
            ownsMotion
            onTransitionEnd={handleTransitionEnd}
        >
            {content}
        </Toast>
    );
};

export default ImageModalV2OperationToast;
